<template>
  <div class="daily-question-container">
    <!-- 顶部导航栏 -->
    <div class="daily-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">每日一题</h1>
      </div>
      <div class="header-right">
        <el-button
          size="small"
          @click="toggleLang"
          :loading="loading"
        >
          {{ displayLang === 'en' ? '切换中文' : 'Switch to English' }}
        </el-button>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          @click="refreshQuestion"
          :loading="loading">
          刷新题目
        </el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-loading
        :loading="loading"
        text="正在加载今日题目..."
        background="rgba(0, 0, 0, 0.8)"
      />
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <i class="el-icon-warning error-icon"></i>
        <p class="error-message">{{ error }}</p>
        <el-button type="primary" @click="refreshQuestion">重新加载</el-button>
      </div>
    </div>

    <!-- 每日一题内容 -->
    <div v-else-if="currentQuestion" class="daily-content">
      <!-- 统计信息 -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-number">{{ streakDays }}</div>
          <div class="stat-label">连续天数</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ totalAnswered }}</div>
          <div class="stat-label">累计答题</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ correctRate }}%</div>
          <div class="stat-label">正确率</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ todayDate }}</div>
          <div class="stat-label">今日日期</div>
        </div>
      </div>

      <!-- 题目卡片 -->
      <div class="question-card">
        <div class="question-header">
          <div class="question-title">
            <span class="question-number">今日题目</span>
            <span class="question-date">{{ todayDate }}</span>
          </div>
          <div class="question-meta">
            <span class="question-difficulty" :class="getDifficultyClass(currentQuestion.difficulty)">
              {{ getDifficultyText(currentQuestion.difficulty) }}
            </span>
            <span class="question-knowledge" v-if="currentQuestion.knowledge">
              {{ currentQuestion.knowledge }}
            </span>
          </div>
        </div>

        <div class="question-content" v-html="formatQuestionContent(getDisplayContent())"></div>

        <!-- 选项显示 -->
        <div class="question-options" v-if="currentQuestion.optionsStr">
          <el-radio-group v-model="userAnswer" @change="onAnswerChange">
            <el-radio
              v-for="option in parseOptions(currentQuestion.optionsStr)"
              :key="option.key"
              :label="option.key"
              class="option-item"
            >
              {{ option.key }}. {{ option.value }}
            </el-radio>
          </el-radio-group>
        </div>

        <!-- 如果没有选项，显示答案输入框 -->
        <div class="question-answer-input" v-else>
          <el-input
            v-model="userAnswer"
            @input="onAnswerChange"
            placeholder="请输入答案"
            maxlength="10"
            show-word-limit
            style="width: 200px"
          ></el-input>
          <span class="answer-tip">（请输入答案，如：A、B、C、D、E 或具体数值）</span>
        </div>

        <!-- 图片上传 -->
        <div class="upload-section">
          <div class="upload-title">上传图片（可选）</div>
          <el-upload
            :action="uploadAction"
            :accept="'image/*'"
            :before-upload="handleBeforeUpload"
            :on-change="handleFileChange"
            :on-remove="handleRemoveImage"
            :on-exceed="handleExceed"
            :multiple="false"
            :limit="1"
            :headers="uploadHeaders"
            :file-list="imageFileList"
            list-type="picture"
            :auto-upload="false"
          >
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">仅支持图片格式，大小请控制在10MB内</div>
          </el-upload>
        </div>

        <!-- 答题状态 -->
        <div v-if="hasAnswered" class="answer-status">
          <div v-if="isAnswerCorrect" class="correct-answer">
            <i class="el-icon-check"></i>
            <span>回答正确！</span>
          </div>
          <div v-else class="incorrect-answer">
            <i class="el-icon-close"></i>
            <span>回答错误，正确答案是：{{ currentQuestion.answer }}</span>
          </div>
        </div>

        <!-- 显示解析（如果已答题） -->
        <div v-if="hasAnswered && currentQuestion.analysis" class="analysis-section">
          <div class="analysis-title">解析：</div>
          <div class="analysis-content" v-html="renderMathFormula(getDisplayAnalysis())"></div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button
            v-if="!hasAnswered"
            type="primary"
            size="large"
            @click="submitAnswer"
            :disabled="!userAnswer"
            :loading="submitting">
            提交答案
          </el-button>
          <el-button
            v-if="hasAnswered"
            type="success"
            size="large"
            @click="nextQuestion"
            :loading="loadingNext">
            下一题
          </el-button>
          <el-button
            v-if="hasAnswered"
            type="info"
            size="large"
            @click="resetAnswer">
            重新答题
          </el-button>
        </div>
      </div>

      <!-- 历史记录 -->
      <div class="history-section" v-if="historyList.length > 0">
        <h3 class="history-title">最近答题记录</h3>
        <div class="history-list">
          <div
            v-for="(record, index) in historyList"
            :key="index"
            class="history-item"
            :class="{ 'correct': record.isCorrect, 'incorrect': !record.isCorrect }"
          >
            <div class="history-date">{{ record.date }}</div>
            <div class="history-status">
              <i :class="record.isCorrect ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>{{ record.isCorrect ? '正确' : '错误' }}</span>
            </div>
            <div class="history-knowledge" v-if="record.knowledge">{{ record.knowledge }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <div class="empty-content">
        <i class="el-icon-document empty-icon"></i>
        <p class="empty-message">今日暂无题目</p>
        <el-button type="primary" @click="refreshQuestion">重新加载</el-button>
      </div>
    </div>

    <!-- 成就弹窗 -->
    <el-dialog
      title="恭喜获得成就！"
      :visible.sync="showAchievementDialog"
      width="400px"
      :before-close="handleCloseAchievementDialog"
    >
      <div class="achievement-content">
        <div class="achievement-icon">
          <i class="el-icon-trophy"></i>
        </div>
        <div class="achievement-title">{{ achievementTitle }}</div>
        <div class="achievement-description">{{ achievementDescription }}</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="showAchievementDialog = false">确定</el-button>
      </span>
    </el-dialog>

    <!-- AI判题结果弹窗 -->
    <el-dialog
      title="AI判题结果"
      :visible.sync="showAiAnalysisDialog"
      width="600px"
      :before-close="handleCloseAiAnalysisDialog"
      :append-to-body="true"
    >
      <div class="ai-analysis-content" v-if="aiAnalysisResult">
        <!-- 题目信息 -->
        <div class="analysis-section">
          <h4 class="section-title">题目</h4>
          <div class="section-content">{{ aiAnalysisResult.question }}</div>
        </div>

        <!-- 答案对比 -->
        <div class="analysis-section">
          <h4 class="section-title">答案对比</h4>
          <div class="answer-comparison">
            <div class="answer-item">
              <span class="answer-label">你的答案：</span>
              <span class="answer-value user-answer">{{ aiAnalysisResult.user_answer }}</span>
            </div>
            <div class="answer-item">
              <span class="answer-label">正确答案：</span>
              <span class="answer-value correct-answer">{{ aiAnalysisResult.correct_answer }}</span>
            </div>
          </div>
        </div>

        <!-- AI分析结果 -->
        <div class="analysis-section" v-if="aiAnalysisResult.ai_analysis">
          <h4 class="section-title">AI分析结果</h4>
          <div class="ai-result">
            <div class="result-item">
              <span class="result-label">判题结果：</span>
              <el-tag
                :type="aiAnalysisResult.ai_analysis.answer_correct ? 'success' : 'danger'"
                size="medium"
              >
                {{ aiAnalysisResult.ai_analysis.answer_correct ? '正确' : '错误' }}
              </el-tag>
            </div>

            <div class="result-item" v-if="aiAnalysisResult.ai_analysis.step_analysis">
              <span class="result-label">步骤分析：</span>
              <div class="result-content">{{ aiAnalysisResult.ai_analysis.step_analysis }}</div>
            </div>

            <div class="result-item" v-if="aiAnalysisResult.ai_analysis.suggestion">
              <span class="result-label">改进建议：</span>
              <div class="result-content suggestion">{{ aiAnalysisResult.ai_analysis.suggestion }}</div>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showAiAnalysisDialog = false">关闭</el-button>
        <el-button type="primary" @click="showAiAnalysisDialog = false">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { judgeSolution } from '@/api/ai'
import { getOneUnattemptedQuestion } from '@/api/amcquestion'
import { addRecord } from '@/api/userQuestionRecord'
import { getTokenInfo } from '@/utils/jwtUtils'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { userStatusAdd } from '@/api/userStatus'

export default {
  name: 'DailyQuestion',
  data() {
    return {
      // 加载状态
      loading: false,
      loadingNext: false,
      // 错误信息
      error: null,
      // 当前题目
      currentQuestion: null,
      // 用户答案
      userAnswer: '',
      // 是否已答题
      hasAnswered: false,
      // 提交状态
      submitting: false,
      // 用户AMC信息
      userAmcInfo: null,
      // 从路由参数获取的repoId
      repoId: null,
      // 当前用户ID（供接口调用）
      currentUserId: null,
      // 显示语言：en | cn，默认英文优先
      displayLang: 'en',

      // 统计数据
      streakDays: 0,
      totalAnswered: 0,
      correctRate: 0,
      todayDate: '',

      // 历史记录
      historyList: [],

      // 成就弹窗
      showAchievementDialog: false,
      achievementTitle: '',
      achievementDescription: '',

      // AI判题结果弹窗
      showAiAnalysisDialog: false,
      aiAnalysisResult: null,

      // 题目列表（用于随机选择）
      questionList: [],
      // 用户已完成的问题ID列表
      completedQuestionIds: [],

      // 上传图片地址（当前题目）
      uploadImageUrl: '',
      // 上传的图片文件对象
      uploadedImageFile: null,
      // 图片上传相关
      uploadAction: 'api/questions/uploadImage',
      uploadHeaders: {},
      imageFileList: []
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.initPage()
    this.initUploadHeaders()
  },
  beforeDestroy() {
    this.showSidebarAndNavbar()
  },
  methods: {
    // 将后端返回的题目结构标准化为前端使用的字段
    normalizeQuestion(raw) {
      if (!raw) return null
      const content = raw.contentCn || raw.contentEn || raw.content || ''
      const optionsStr = raw.optionsStr || raw.options || ''
      const analysis = raw.analysisCn || raw.analysisEn || raw.analysis || raw.judge || ''
      return {
        id: raw.id,
        content,
        contentCn: raw.contentCn || '',
        contentEn: raw.contentEn || '',
        optionsStr,
        answer: raw.answer || raw.correct || '',
        analysis,
        analysisCn: raw.analysisCn || '',
        analysisEn: raw.analysisEn || '',
        knowledge: raw.knowledge || '',
        difficulty: typeof raw.difficulty === 'number' ? raw.difficulty : 0.5,
        // 兼容其它可能用到的字段
        url: raw.url || raw.image || ''
      }
    },
    // 语言切换
    toggleLang() {
      this.displayLang = this.displayLang === 'en' ? 'cn' : 'en'
    },
    // 获取展示用题干
    getDisplayContent() {
      if (!this.currentQuestion) return ''
      if (this.displayLang === 'en') {
        return this.currentQuestion.contentEn || this.currentQuestion.content || this.currentQuestion.contentCn || ''
      }
      return this.currentQuestion.contentCn || this.currentQuestion.content || this.currentQuestion.contentEn || ''
    },
    // 获取展示用解析
    getDisplayAnalysis() {
      if (!this.currentQuestion) return ''
      if (this.displayLang === 'en') {
        return this.currentQuestion.analysisEn || this.currentQuestion.analysis || this.currentQuestion.analysisCn || ''
      }
      return this.currentQuestion.analysisCn || this.currentQuestion.analysis || this.currentQuestion.analysisEn || ''
    },
    // 初始化上传请求头
    initUploadHeaders() {
      const { getToken } = require('@/utils/auth')
      this.uploadHeaders = { Authorization: getToken() }
    },

    // 处理文件变化
    handleFileChange(file, fileList) {
      console.log('文件变化:', file, fileList)
      if (file.raw) {
        this.uploadedImageFile = file.raw
        this.imageFileList = fileList
        console.log('保存文件对象:', this.uploadedImageFile)
      }
    },

    // 处理图片上传前的钩子
    handleBeforeUpload(file) {
      console.log('上传前检查文件:', file)
      return false // 阻止自动上传
    },

    // 处理图片移除
    handleRemoveImage(file) {
      console.log('移除文件:', file)
      this.uploadedImageFile = null
      this.imageFileList = []
    },

    // 处理文件超出限制
    handleExceed() {
      this.$message.warning('每次只能上传 1 个文件')
    },

    // 隐藏侧边栏和导航栏
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = 'none'
      }
      if (navbar) {
        navbar.style.display = 'none'
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },

    // 恢复侧边栏和导航栏
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = ''
      }
      if (navbar) {
        navbar.style.display = ''
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },

    // 初始化页面
    initPage() {
      // 从路由参数获取repoId
      this.repoId = this.$route.query.repoId

      if (!this.repoId) {
        this.$message.error('缺少题库ID参数')
        this.goBack()
        return
      }

      this.todayDate = this.getTodayDate()
      this.loadUserData()
    },

    // 加载用户数据
    async loadUserData() {
      try {
        // 获取当前用户ID
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null

        if (!userId) {
          throw new Error('无法获取用户信息，请重新登录')
        }

        // 获取用户AMC信息（已废弃，不再使用AMC专用接口）
        // const amcResponse = await getAmcInfoByUserId(userId)
        // console.log('用户AMC信息:', amcResponse.data)
        // if (amcResponse.data && amcResponse.data.length > 0) {
        //   this.userAmcInfo = amcResponse.data[0]
        //   this.loadStatistics()
        // }

        // 记录当前用户ID供后续接口调用
        this.currentUserId = userId
        this.loadDailyQuestion()
      } catch (error) {
        console.error('加载用户数据失败:', error)
        this.error = error.message || '加载用户数据失败'
      }
    },

    // 处理用户问题记录数据
    processUserQuestionRecords(records) {
      console.log('处理用户问题记录:', records)
      // 保存用户已完成的问题ID列表
      if (Array.isArray(records)) {
        this.completedQuestionIds = records.map(record => record.quesId)
        console.log(`用户已完成的问题ID列表:`, this.completedQuestionIds)
      } else {
        this.completedQuestionIds = []
      }
    },

    // 加载统计数据
    loadStatistics() {
      // 从localStorage获取统计数据
      const stats = JSON.parse(localStorage.getItem('daily_question_stats') || '{}')
      this.streakDays = stats.streakDays || 0
      this.totalAnswered = stats.totalAnswered || 0
      this.correctRate = stats.correctRate || 0

      // 加载历史记录
      this.loadHistory()
    },

    // 加载历史记录
    loadHistory() {
      const history = JSON.parse(localStorage.getItem('daily_question_history') || '[]')
      this.historyList = history.slice(0, 7) // 只显示最近7天
    },

    // 加载每日一题：从后端获取“该用户在该题库的一道未作答题目”，不再请求全部题目
    async loadDailyQuestion() {
      this.loading = true
      this.error = null

      try {
        // 优先：请求一题未作答
        const oneResp = await getOneUnattemptedQuestion(this.currentUserId, this.repoId)
        const one = oneResp && oneResp.data
        if (one) {
          this.currentQuestion = this.normalizeQuestion(one)
        } else {
          this.currentQuestion = null
          this.error = '暂无未作答题目'
        }
      } catch (error) {
        console.error('获取题目失败:', error)
        this.error = '获取题目失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    // 加载下一题（另见下方 nextQuestion 实现）

    // 选择今日题目
    selectTodayQuestion() {
      if (this.questionList.length === 0) return

      // 筛选出用户未完成过的题目
      const uncompletedQuestions = this.questionList.filter(question =>
        !this.completedQuestionIds.includes(question.id)
      )

      console.log(`总题目数: ${this.questionList.length}`)
      console.log(`已完成题目数: ${this.completedQuestionIds.length}`)
      console.log(`未完成题目数: ${uncompletedQuestions.length}`)

      if (uncompletedQuestions.length === 0) {
        // 如果所有题目都已完成，随机选择一道题
        console.log('所有题目都已完成，随机选择一道题')
        const randomIndex = Math.floor(Math.random() * this.questionList.length)
        this.currentQuestion = this.questionList[randomIndex]
      } else {
        // 从未完成的题目中随机选择一道
        const randomIndex = Math.floor(Math.random() * uncompletedQuestions.length)
        this.currentQuestion = uncompletedQuestions[randomIndex]
        console.log(`从未完成题目中选择: 题目ID ${this.currentQuestion.id}`)
      }

      // 重置答题状态
      this.userAnswer = ''
      this.hasAnswered = false
      this.uploadImageUrl = ''
      this.uploadedImageFile = null
      this.imageFileList = []
    },

    // 解析选项字符串
    parseOptions(optionsStr) {
      if (!optionsStr) return []

      const options = []
      const optionRegex = /\(([A-E])\)\s*([^;]+)/g
      let match

      while ((match = optionRegex.exec(optionsStr)) !== null) {
        options.push({
          key: match[1],
          value: match[2].trim()
        })
      }

      return options
    },

    // 获取选项的完整内容（包含选项字母和内容）
    getFullOptionContent(optionKey) {
      if (!optionKey || !this.currentQuestion?.optionsStr) {
        return optionKey || ''
      }

      const options = this.parseOptions(this.currentQuestion.optionsStr)
      const option = options.find(opt => opt.key === optionKey)

      if (option) {
        return `${option.key} ${option.value}`
      }

      return optionKey
    },

    // 答案变化处理
    onAnswerChange() {
      console.log('用户答案:', this.userAnswer)
    },

    // 检查答案是否正确
    isAnswerCorrect() {
      return this.userAnswer === this.currentQuestion.answer
    },

    // 获取难度类
    getDifficultyClass(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      return `difficulty-${level}`
    },

    // 根据数字难度获取难度级别
    getDifficultyLevel(difficulty) {
      if (difficulty <= 0.3) return 'easy'
      if (difficulty <= 0.7) return 'medium'
      return 'hard'
    },

    // 获取难度文本
    getDifficultyText(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      const textMap = {
        'easy': '简单',
        'medium': '中等',
        'hard': '困难'
      }
      return textMap[level] || '未知'
    },

    // 格式化题目内容
    formatQuestionContent(content) {
      if (!content) return '暂无题目内容'
      return content.replace(/\n/g, '<br>')
    },

    // 提交答案
    async submitAnswer() {
      if (!this.userAnswer) {
        this.$message.warning('请先选择答案')
        return
      }

      this.submitting = true
      // 全屏等待中加载
      let loadingInstance = null
      try {
        loadingInstance = this.$loading({
          lock: true,
          text: '等待中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.5)'
        })
      } catch (e) {
        // 若 $loading 不可用则忽略
      }

      try {
        let isCorrect = false

        // 判断是否上传了图片
        console.log('uploadImageUrl:', this.uploadImageUrl)
        console.log('uploadedImageFile:', this.uploadedImageFile)
        console.log('uploadImageUrl type:', typeof this.uploadImageUrl)
        console.log('uploadImageUrl length:', this.uploadImageUrl ? this.uploadImageUrl.length : 0)

        if (this.uploadedImageFile) {
          console.log('检测到上传了图片，调用 judgeSolution 方法')
          // 如果上传了图片，调用 judgeSolution 方法
          isCorrect = await this.judgeAnswerWithImage()
        } else {
          console.log('没有上传图片，使用原有逻辑')
          // 如果没有上传图片，沿用原来的逻辑
          isCorrect = this.isAnswerCorrect()
        }

        // 判题完成后再标记为已答题
        this.hasAnswered = true

        // 更新统计数据
        this.updateStatistics(isCorrect)

        // 保存答题记录
        this.saveAnswerRecord(isCorrect)

        // 将记录写入后端
        this.persistAnswerRecord(isCorrect)

        // 写入 AMC 状态记录
        this.persistAmcStatusRecord(isCorrect)

        // 检查成就
        this.checkAchievements()

        if (isCorrect) {
          this.$message.success('回答正确！')
        } else {
          this.$message.error(`回答错误，正确答案是：${this.currentQuestion.answer}`)
        }
      } catch (error) {
        console.error('提交答案失败:', error)
        this.$message.error('提交答案失败，请重试')
      } finally {
        if (loadingInstance && typeof loadingInstance.close === 'function') {
          loadingInstance.close()
        }
        this.submitting = false
      }
    },

    // 通过图片判断答案
    async judgeAnswerWithImage() {
      try {
        console.log('开始调用 judgeAnswerWithImage 方法')
        console.log('当前题目:', this.currentQuestion)
        console.log('用户答案:', this.userAnswer)
        console.log('图片URL:', this.uploadImageUrl)

        // 创建 FormData 对象
        const formData = new FormData()

        // 添加题目内容
        formData.append('question', this.currentQuestion.content)

        // 添加用户答案（包含完整选项内容）
        const fullUserAnswer = this.getFullOptionContent(this.userAnswer)
        formData.append('user_answer', fullUserAnswer)

        // 添加正确答案（包含完整选项内容）
        const fullCorrectAnswer = this.getFullOptionContent(this.currentQuestion.answer)
        formData.append('correct_answer', fullCorrectAnswer)

        // 添加解析内容
        formData.append('explanation', this.currentQuestion.analysis || this.currentQuestion.judge || '')

        // 添加图片文件
        // 直接使用上传的文件对象
        if (this.uploadedImageFile) {
          formData.append('image', this.uploadedImageFile, this.uploadedImageFile.name)
        } else {
          throw new Error('没有找到上传的图片文件')
        }

        // 调用 judgeSolution API
        console.log('准备调用 judgeSolution API')
        console.log('FormData 内容:')
        for (let [key, value] of formData.entries()) {
          console.log(key, ':', value)
        }

        let loadingService = null
        try {
          loadingService = this.$loading({ lock: true, text: 'AI分析中...', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.35)' })
          const response = await judgeSolution(formData)
          console.log('judgeSolution API 响应:', response)

          // 统一解析响应并弹窗
          this.handleJudgeQuestionResponse(response)

        // 从已解析的 aiAnalysisResult 提取正确性；若不存在则回退
        const isCorrectFromAi = this.aiAnalysisResult && this.aiAnalysisResult.ai_analysis
          ? !!this.aiAnalysisResult.ai_analysis.answer_correct
          : null

          if (isCorrectFromAi === null) {
            console.warn('AI结果缺失，使用原有本地判断逻辑')
            return this.isAnswerCorrect()
          }
          console.log('AI 判断结果:', isCorrectFromAi)
          return isCorrectFromAi
        } finally {
          if (loadingService) loadingService.close()
        }
      } catch (error) {
        console.error('图片判断失败:', error)
        this.$message.warning('图片判断失败，使用原有逻辑判断')
        // 如果图片判断失败，回退到原来的逻辑
        return this.isAnswerCorrect()
      }
    },

    // 将答题记录写入后端
    async persistAnswerRecord(isCorrect) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) return

        const payload = {
          userId: userId,
          quesId: this.currentQuestion?.id,
          quesContent: this.currentQuestion?.content,
          userAnswer: this.getFullOptionContent(this.userAnswer),
          judge: this.currentQuestion?.analysis || this.currentQuestion?.judge || '',
          mynote: '',
          isReview: 0,
          knowledge: this.currentQuestion?.knowledge || '',
          isRight: isCorrect ? 1 : 0,
          repoId: Number(this.repoId) || this.repoId,
          time: new Date().toISOString(),
          aiAnalysis: this.aiAnalysisResult ? JSON.stringify(this.aiAnalysisResult) : null
        }

        await addRecord(payload)
        console.log('已写入答题记录到后端', payload)
      } catch (e) {
        console.warn('写入答题记录失败:', e)
      }
    },

    // 写入 AMC 状态记录
    async persistAmcStatusRecord(isCorrect) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId) return

        const payload = {
          userId: userId,
          quesList: String(this.currentQuestion?.id || ''),
          count: 1,
          rightCount: isCorrect ? 1 : 0,
          source: '每日一题'
        }

        await userStatusAdd(payload)
        console.log('已写入 AMC 状态记录', payload)
      } catch (e) {
        console.warn('写入 AMC 状态记录失败:', e)
      }
    },

    // 更新统计数据
    updateStatistics(isCorrect) {
      const stats = JSON.parse(localStorage.getItem('daily_question_stats') || '{}')

      // 更新总答题数
      stats.totalAnswered = (stats.totalAnswered || 0) + 1

      // 更新正确率
      const totalCorrect = (stats.totalCorrect || 0) + (isCorrect ? 1 : 0)
      stats.correctRate = Math.round((totalCorrect / stats.totalAnswered) * 100)
      stats.totalCorrect = totalCorrect

      // 更新连续天数
      const today = this.getTodayDate()
      const lastAnswerDate = stats.lastAnswerDate

      if (lastAnswerDate === today) {
        // 今天已经答过题，不更新连续天数
      } else if (lastAnswerDate === this.getYesterdayDate()) {
        // 昨天答过题，连续天数+1
        stats.streakDays = (stats.streakDays || 0) + 1
      } else {
        // 中断了连续答题，重置为1
        stats.streakDays = 1
      }

      stats.lastAnswerDate = today

      localStorage.setItem('daily_question_stats', JSON.stringify(stats))

      // 更新页面显示
      this.streakDays = stats.streakDays
      this.totalAnswered = stats.totalAnswered
      this.correctRate = stats.correctRate
    },

    // 保存答题记录
    saveAnswerRecord(isCorrect) {
      const history = JSON.parse(localStorage.getItem('daily_question_history') || '[]')

      const record = {
        date: this.todayDate,
        questionId: this.currentQuestion.id,
        isCorrect: isCorrect,
        knowledge: this.currentQuestion.knowledge,
        answer: this.userAnswer,
        correctAnswer: this.currentQuestion.answer
      }

      // 检查今天是否已经答过题
      const todayIndex = history.findIndex(item => item.date === this.todayDate)
      if (todayIndex >= 0) {
        history[todayIndex] = record
      } else {
        history.unshift(record)
      }

      // 只保留最近30天的记录
      if (history.length > 30) {
        history.splice(30)
      }

      localStorage.setItem('daily_question_history', JSON.stringify(history))
      this.loadHistory()
    },

    // 检查成就
    checkAchievements() {
      const stats = JSON.parse(localStorage.getItem('daily_question_stats') || '{}')

      // 连续答题成就
      if (stats.streakDays === 7 && !stats.achievement7) {
        this.showAchievement('连续答题7天', '恭喜你获得"坚持不懈"成就！')
        stats.achievement7 = true
      } else if (stats.streakDays === 30 && !stats.achievement30) {
        this.showAchievement('连续答题30天', '恭喜你获得"持之以恒"成就！')
        stats.achievement30 = true
      }

      // 正确率成就
      if (stats.correctRate >= 80 && stats.totalAnswered >= 10 && !stats.achievement80) {
        this.showAchievement('正确率80%', '恭喜你获得"学霸"成就！')
        stats.achievement80 = true
      }

      localStorage.setItem('daily_question_stats', JSON.stringify(stats))
    },

    // 显示成就
    showAchievement(title, description) {
      this.achievementTitle = title
      this.achievementDescription = description
      this.showAchievementDialog = true
    },

    // 下一题：直接重新从后端拉取一题
    async nextQuestion() {
      this.loadingNext = true
      try {
        await this.loadDailyQuestion()
      } finally {
        this.loadingNext = false
      }
    },

    // 重置答案
    resetAnswer() {
      this.userAnswer = ''
      this.hasAnswered = false
    },

    // 刷新题目
    refreshQuestion() {
      this.loadDailyQuestion()
    },

    // 获取今日日期
    getTodayDate() {
      const today = new Date()
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    },

    // 获取昨日日期
    getYesterdayDate() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`
    },

    // 关闭成就弹窗
    handleCloseAchievementDialog() {
      this.showAchievementDialog = false
    },

    // 关闭AI分析弹窗
    handleCloseAiAnalysisDialog() {
      this.showAiAnalysisDialog = false
      this.aiAnalysisResult = null
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 渲染数学公式
    renderMathFormula(text) {
      if (!text) return ''

      try {
        let html = String(text)
        // 1) 行内公式: \(...\)
        // 修复：使用正确的正则表达式 \\\(([\s\S]+?)\\\) 匹配包含括号的公式
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
          try {
            return katex.renderToString(formula, {
              throwOnError: false,
              displayMode: false
            })
          } catch (e) {
            console.warn('KaTeX rendering error:', e)
            return match
          }
        })
        // 2) 块级公式: \[...\]
        // 修复：使用 [\s\S]+? 而不是 [^\]]+ 以正确匹配复杂公式
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
          try {
            return katex.renderToString(formula, {
              throwOnError: false,
              displayMode: true
            })
          } catch (e) {
            console.warn('KaTeX rendering error:', e)
            return match
          }
        })
        return html
      } catch (e) {
        console.warn('Math formula rendering error:', e)
        return text
      }
    },

    // 显示判题结果弹窗
    showJudgeResultDialog(judgeResult) {
      console.log('显示判题结果弹窗:', judgeResult)

      // 设置AI分析结果数据
      this.aiAnalysisResult = judgeResult

      // 显示弹窗
      this.showAiAnalysisDialog = true

      // 可选：根据判题结果显示不同的消息提示
      if (judgeResult.ai_analysis && judgeResult.ai_analysis.answer_correct) {
        this.$message.success('AI判题：回答正确！')
      } else {
        this.$message.warning('AI判题：回答需要改进')
      }
    },

    // 测试判题结果弹窗方法
    testJudgeResultDialog() {
      // 使用你提供的示例数据
      const testResult = {
        question: "The average weight of 6 boys is 150 pounds and the average weight of 4 girls is 120 pounds. The average weight of the 10 children is",
        user_answer: "A 135 pounds",
        correct_answer: "C 138 pounds",
        ai_analysis: {
          answer_correct: true,
          step_analysis: "解题步骤清晰合理，先分别计算男孩和女孩的总体重，再计算所有孩子的总体重，最后求出平均体重。",
          suggestion: ""
        },
        status: "success"
      }

      // 调用显示弹窗方法
      this.showJudgeResultDialog(testResult)
    },

    // 处理API返回的判题结果并显示弹窗（与 QuickPractice 对齐的宽松解析器）
    handleJudgeQuestionResponse(apiResponse) {
      try {
        console.log('处理判题API响应:', apiResponse)

        // 统一提取：data -> result -> 本体
        const raw = apiResponse && apiResponse.data !== undefined && (apiResponse.status !== undefined || apiResponse.headers !== undefined)
          ? apiResponse.data
          : apiResponse
        let payload = raw
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload) } catch (e) { /* ignore */ }
        }
        const data = (payload && (payload.result ?? payload.data)) || payload || {}

        // 规格化结构
        const normalized = {
          question: data?.question || this.currentQuestion?.content || '',
          user_answer: data?.user_answer || data?.userAnswer || this.getFullOptionContent(this.userAnswer),
          correct_answer: data?.correct_answer || data?.correctAnswer || this.getFullOptionContent(this.currentQuestion?.answer),
          ai_analysis: {
            answer_correct: !!(data?.ai_analysis?.answer_correct ?? data?.answer_correct ?? data?.correct),
            step_analysis: data?.ai_analysis?.step_analysis ?? data?.step_analysis ?? data?.analysis ?? '',
            suggestion: data?.ai_analysis?.suggestion ?? data?.suggestion ?? ''
          },
          status: data?.status || 'ok'
        }

        this.showJudgeResultDialog(normalized)
        return true
      } catch (error) {
        console.error('处理判题结果时出错:', error)
        this.$message.error('处理判题结果失败')
        return false
      }
    }
  }
}
</script>
<style scoped>
.daily-question-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 0;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 顶部导航栏 */
.daily-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-right {
  display: flex;
  align-items: center;
}

.back-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #f5f5f5;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

/* 统计信息 */
.stats-section {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stat-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
}

/* 加载和错误状态 */
.loading-container, .error-container, .empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-content, .empty-content {
  text-align: center;
  padding: 40px;
}

.error-icon, .empty-icon {
  font-size: 48px;
  color: #f56c6c;
  margin-bottom: 20px;
}

.empty-icon {
  color: #c0c4cc;
}

.error-message, .empty-message {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

/* 每日一题内容 */
.daily-content {
  padding: 20px;
}

/* 题目卡片 */
.question-card {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.question-title {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.question-number {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.question-date {
  font-size: 14px;
  color: #666;
}

.question-meta {
  display: flex;
  gap: 10px;
  align-items: center;
}

.question-difficulty {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 500;
}

.difficulty-easy {
  background: #f6ffed;
  color: #52c41a;
}

.difficulty-medium {
  background: #fff7e6;
  color: #fa8c16;
}

.difficulty-hard {
  background: #fff2f0;
  color: #ff4d4f;
}

.question-knowledge {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  background: #f0f0f0;
  color: #666;
}

.question-content {
  margin-bottom: 25px;
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.question-options {
  margin-bottom: 25px;
}

.option-item {
  display: block;
  margin-bottom: 12px;
  font-size: 15px;
  padding: 8px 0;
}

.question-answer-input {
  margin-bottom: 25px;
}

.upload-section {
  margin: 20px 0 25px 0;
}

.upload-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.answer-tip {
  font-size: 12px;
  color: #999;
  margin-left: 10px;
}

.answer-status {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.correct-answer {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.incorrect-answer {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.analysis-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 25px;
}

.analysis-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  font-size: 16px;
}

.analysis-content {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

/* 历史记录 */
.history-section {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.history-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.history-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.history-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.history-item.correct {
  background: #f6ffed;
  border-color: #b7eb8f;
}

.history-item.incorrect {
  background: #fff2f0;
  border-color: #ffccc7;
}

.history-date {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.history-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 500;
}

.history-item.correct .history-status {
  color: #52c41a;
}

.history-item.incorrect .history-status {
  color: #ff4d4f;
}

.history-knowledge {
  font-size: 12px;
  color: #666;
}

/* 成就弹窗 */
.achievement-content {
  text-align: center;
  padding: 20px;
}

.achievement-icon {
  font-size: 48px;
  color: #faad14;
  margin-bottom: 15px;
}

.achievement-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.achievement-description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

/* AI分析弹窗样式 */
.ai-analysis-content {
  padding: 10px 0;
}

.analysis-section {
  margin-bottom: 25px;
}

.analysis-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.section-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #1890ff;
}

.answer-comparison {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.answer-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
}

.answer-label {
  font-weight: 500;
  color: #333;
  min-width: 80px;
}

.answer-value {
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  flex: 1;
}

.user-answer {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.correct-answer {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.ai-result {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.result-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.result-content {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  border-left: 3px solid #d9d9d9;
  white-space: pre-wrap;
}

.result-content.suggestion {
  background: #fff7e6;
  border-left-color: #faad14;
  color: #d46b08;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .daily-header {
    padding: 10px 15px;
    flex-direction: column;
    gap: 10px;
  }

  .header-left {
    width: 100%;
    justify-content: space-between;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .stats-section {
    flex-direction: column;
    gap: 15px;
    padding: 15px;
  }

  .question-card {
    padding: 20px;
  }

  .question-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .question-meta {
    width: 100%;
    justify-content: flex-start;
  }

  .action-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .history-list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .stat-number {
    font-size: 24px;
  }

  .question-content {
    font-size: 14px;
  }

  .option-item {
    font-size: 14px;
  }
}
</style>
